import { Loader } from '@googlemaps/js-api-loader';

const isSSR = typeof window === 'undefined';

// Only grab the “context” (and set up the loader configuration) on the client
let context = {};
let loader = null;

if (!isSSR) {
    context = window.__INITIAL_STATE__ || {};

    // Configure the Google Maps loader
    const loaderConfig = {
        version: 'weekly',
        libraries: ['drawing', 'geometry', 'places'],
    };

    if (context.isDevelopment) {
        loaderConfig.apiKey = context.googleMapKey ? context.googleMapKey.replace('key=', '') : '';
    } else {
        loaderConfig.client = context.googleMapKey ? context.googleMapKey.replace('client=', '') : '';
    }

    loader = new Loader(loaderConfig);
}

// A simple cache of “hasStreetView” results or “loading” states
const streetViewCheckList = {}; // { [key: string]: boolean | 'loading' }
const callbacksMap = {}; // { [key: string]: Array<Function> }

/**
 * Loads the Google Maps JS API (client‐only).
 * On SSR, this returns a resolved promise immediately (no side‐effects).
 */
export function loadGMap() {
    if (isSSR) {
        // on server, do nothing
        return Promise.resolve();
    }
    return loader.load();
}

/**
 * Checks whether Street View imagery exists at a given location.
 *
 * @param {Object} opt
 * @param {string} [opt.address] – e.g. "1600 Amphitheatre Parkway, Mountain View, CA"
 * @param {number} [opt.lat] – latitude
 * @param {number} [opt.lng] – longitude
 * @param {Function} opt.callback – function(boolean) to receive true/false
 */
export function hasStreetView(opt) {
    if (isSSR) {
        // Immediately invoke callback(false) on the server
        if (typeof opt.callback === 'function') {
            opt.callback(false);
        }
        return;
    }

    return loadGMap().then(() => {
        let key = null;

        // Determine the cache key; lat/lng takes priority
        if (opt.lat != null && opt.lng != null) {
            key = `${opt.lat},${opt.lng}`;
        } else if (opt.address) {
            key = encodeURIComponent(opt.address);
        } else {
            // Neither lat/lng nor address provided
            opt.callback(false);
            return;
        }

        const cached = streetViewCheckList[key];
        if (cached !== undefined) {
            // If we already have a boolean → call immediately
            if (cached !== 'loading') {
                opt.callback(cached);
                return;
            }
            // Otherwise, status is 'loading' → just queue the callback
            callbacksMap[key].push(opt.callback);
            return;
        }

        // First request for this key: mark as loading and initialize callback queue
        streetViewCheckList[key] = 'loading';
        callbacksMap[key] = [opt.callback];

        const metaDataPath = `/maps/api/streetview/metadata?location=${key}&${context.googleMapKey}`;

        if (context.isDevelopment) {
            // Directly hit Google’s metadata endpoint
            fetch(`https://maps.googleapis.com${metaDataPath}`, { method: 'GET' })
                .then((response) => response.json())
                .then((res) => {
                    const hasSV = res && res.status === 'OK';
                    streetViewCheckList[key] = hasSV;
                    callbacksMap[key].forEach((cb) => cb(hasSV));
                    delete callbacksMap[key];
                })
                .catch(() => {
                    streetViewCheckList[key] = false;
                    callbacksMap[key].forEach((cb) => cb(false));
                    delete callbacksMap[key];
                });
        } else {
            // In production, sign via backend then fetch metadata
            const body = { url: metaDataPath };
            fetch(`${context.appUrl}webservices/getgooglemapurlwithsignature/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json; charset=UTF-8' },
                body: JSON.stringify(body),
            })
                .then((response) => response.json())
                .then((data) => {
                    return fetch(`https://maps.googleapis.com${data.url}`, {
                        method: 'GET',
                    });
                })
                .then((response) => response.json())
                .then((res) => {
                    const hasSV = res && res.status === 'OK';
                    streetViewCheckList[key] = hasSV;
                    callbacksMap[key].forEach((cb) => cb(hasSV));
                    delete callbacksMap[key];
                })
                .catch(() => {
                    streetViewCheckList[key] = false;
                    callbacksMap[key].forEach((cb) => cb(false));
                    delete callbacksMap[key];
                });
        }
    });
}
